/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useTranslation } from "react-i18next";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";

// Internationalization languages
import { SUPPORTED_LANGUAGES, getLanguageConfig } from "i18n/languages";

// Material Dashboard 2 PRO React examples
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavList from "examples/Sidenav/SidenavList";
import SidenavItem from "examples/Sidenav/SidenavItem";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const { i18n } = useTranslation();
  const currentLanguageConfig = getLanguageConfig(i18n.language);
  const [openCollapse, setOpenCollapse] = useState(false);
  const [openNestedCollapse, setOpenNestedCollapse] = useState(false);
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const items = pathname.split("/").slice(1);
  const itemParentName = items[1];
  const itemName = items[items.length - 1];

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);
  const handleLanguageChange = (event) => {
    const language = event.target.value;
    localStorage.setItem("language", language);
    i18n.changeLanguage(language);
  };

  useEffect(() => {
    setOpenCollapse(collapseName);
    setOpenNestedCollapse(itemParentName);
  }, []);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the nested collapse items from the routes.js
  const renderNestedCollapse = (collapse) => {
    const template = collapse.map(({ name, route, key, href }) =>
      href ? (
        <Link
          key={key}
          href={href}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavItem name={name} nested />
        </Link>
      ) : (
        <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
          <SidenavItem name={name} active={route === pathname} nested />
        </NavLink>
      )
    );

    return template;
  };
  // Render the all the collpases from the routes.js
  const renderCollapse = (collapses) =>
    collapses.map(({ name, collapse, route, href, key }) => {
      let returnValue;

      if (collapse) {
        returnValue = (
          <SidenavItem
            key={key}
            color={color}
            name={name}
            active={key === itemParentName ? "isParent" : false}
            open={openNestedCollapse === key}
            onClick={({ currentTarget }) =>
              openNestedCollapse === key && currentTarget.classList.contains("MuiListItem-root")
                ? setOpenNestedCollapse(false)
                : setOpenNestedCollapse(key)
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavItem>
        );
      } else {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavItem color={color} name={name} active={key === itemName} />
          </Link>
        ) : (
          <NavLink to={route} key={key} sx={{ textDecoration: "none" }}>
            <SidenavItem color={color} name={name} active={key === itemName} />
          </NavLink>
        );
      }
      return <SidenavList key={key}>{returnValue}</SidenavList>;
    });

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(
    ({ type, name, icon, title, collapse, noCollapse, key, href, route, badge }) => {
      let returnValue;

      if (type === "collapse") {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavCollapse
                name={name}
                icon={icon}
                badge={badge}
                active={key === collapseName}
                noCollapse={noCollapse}
              />
            </Link>
          );
        } else if (noCollapse && route) {
          returnValue = (
            <NavLink to={route} key={key}>
              <SidenavCollapse
                name={name}
                icon={icon}
                badge={badge}
                noCollapse={noCollapse}
                active={key === collapseName}
              >
                {collapse ? renderCollapse(collapse) : null}
              </SidenavCollapse>
            </NavLink>
          );
        } else {
          returnValue = (
            <SidenavCollapse
              key={key}
              name={name}
              icon={icon}
              badge={badge}
              active={key === collapseName}
              open={openCollapse === key}
              onClick={() => (openCollapse === key ? setOpenCollapse(false) : setOpenCollapse(key))}
            >
              {collapse ? renderCollapse(collapse) : null}
            </SidenavCollapse>
          );
        }
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      }

      return returnValue;
    }
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
      sx={{
        display: "flex",
        flexDirection: "column",
        // height: "100vh",
      }}
    >
      <MDBox pt={3} pb={1} px={3} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
          {/* {brand && <MDBox component="img" src={brand} alt="Brand" width="2rem" mr={1} />} */}
          {!miniSidenav && (
            <MDBox sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}>
              <MDTypography component="h2" fontWeight="medium" color="primary" display="flex" alignItems="center">
                {brandName}
              </MDTypography>
            </MDBox>
          )}
          {!miniSidenav && currentLanguageConfig.brandTransliteration && (
            <MDTypography
              variant="button"
              fontWeight="medium"
              color={textColor}
              sx={{
                fontSize: "0.85rem",
                lineHeight: 1.2,
                mt: 0.25,
                opacity: 0.9,
                letterSpacing: "0.3px",
              }}
            >
              {currentLanguageConfig.brandTransliteration}
            </MDTypography>
          )}
        </MDBox>
        {!miniSidenav && (
          <MDBox mt={2} px={1} width="100%">
            <Select
              fullWidth
              value={currentLanguageConfig.code}
              onChange={handleLanguageChange}
              size="small"
              displayEmpty
              inputProps={{ "aria-label": "Language Selection" }}
              renderValue={(selectedCode) => {
                const config = getLanguageConfig(selectedCode);
                return (
                  <MDBox display="flex" alignItems="center" sx={{ overflow: "hidden" }}>
                    <Icon
                      color="primary"
                      sx={{
                        fontSize: "1.15rem !important",
                        mr: 0.75,
                        opacity: 0.9,
                      }}
                    >
                      language
                    </Icon>
                    <MDTypography
                      variant="button"
                      fontWeight="medium"
                      color="inherit"
                      sx={{
                        fontSize: "0.8125rem",
                        lineHeight: 1,
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {config.display}
                    </MDTypography>
                  </MDBox>
                );
              }}
              sx={{
                width: "100%",
                height: "1.5rem",
                color: textColor === "dark" ? "#344767" : "#ffffff",
                backgroundColor:
                  textColor === "dark"
                    ? "rgba(0, 0, 0, 0.04)"
                    : "rgba(255, 255, 255, 0.12)",
                borderRadius: "8px",
                fontSize: "0.875rem",
                textAlign: "left",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  py: 0.6,
                  px: 1.25,
                  color: textColor === "dark" ? "#344767" : "#ffffff",
                  fontWeight: 500,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor:
                    textColor === "dark"
                      ? "rgba(0, 0, 0, 0.2)"
                      : "rgba(255, 255, 255, 0.35)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor:
                    textColor === "dark"
                      ? "rgba(0, 0, 0, 0.45)"
                      : "rgba(255, 255, 255, 0.7)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#2195f36c",
                },
                "& .MuiSelect-icon": {
                  color: textColor === "dark" ? "#344767" : "#ffffff",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: darkMode ? "#202940" : "#ffffff",
                    color: darkMode ? "#ffffff" : "#344767",
                    borderRadius: "8px",
                    mt: 0.5,
                    boxShadow: 4,
                  },
                },
              }}
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <MenuItem
                  key={lang.code}
                  value={lang.code}
                  disabled={lang.disabled}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 1,
                    px: 1.5,
                    fontSize: "0.85rem",
                  }}
                >
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <MDTypography
                      variant="button"
                      fontWeight={
                        lang.code === getLanguageConfig(i18n.language).code
                          ? "bold"
                          : "regular"
                      }
                      color="inherit"
                    >
                      {lang.label}
                    </MDTypography>
                    {lang.englishLabel && lang.label !== lang.englishLabel && (
                      <MDTypography variant="caption" color="text" sx={{ opacity: 0.65 }}>
                        ({lang.englishLabel})
                      </MDTypography>
                    )}
                  </MDBox>
                  {lang.badge && (
                    <MDBadge
                      variant="contained"
                      color="secondary"
                      size="xs"
                      badgeContent={lang.badge}
                      container
                    />
                  )}
                </MenuItem>
              ))}
            </Select>
          </MDBox>
        )}
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>{renderRoutes}</List>
      <MDBox px={2} pb={2}>
        
      </MDBox>
      <MDBox mt="auto" px={2} pb={2}>
        {!miniSidenav && (
          <MDTypography
            variant="caption"
            color="text"
            sx={{ display: "block", textAlign: "center", fontSize: 16, mt: 2, fontWeight: "bold" }}
          >
            Since 2010
          </MDTypography>
        )}
        {!miniSidenav && (
          <Link
            href="https://www.lakhandahana.com"
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{ display: "block", textAlign: "center", color: "primary.main", fontWeight: "bold", fontSize:14, mt: 2 }}
          >
            www.lakhandahana.com
          </Link>
        )}
      </MDBox>
      
      {/* <MDBox mt="auto" px={2} pb={2} display="flex" justifyContent="center"> */}
        {!miniSidenav && (
          <MDBox mt={0} px={2} pb={2} width="100%">
            <MDBox
              component="button"
              type="button"
              onClick={() => {
                localStorage.removeItem('googleToken');
                window.location.reload();
              }}
              sx={{
                width: "100%",
                py: 1,
                px: 2,
                backgroundColor: "grey.500",
                color: "white",
                border: "none",
                borderRadius: 1,
                fontWeight: "bold",
                fontSize: 14,
                cursor: "pointer",
                mt: 2,
                transition: "background 0.2s",
                "&:hover": {
                  backgroundColor: "error.dark",
                },
              }}
            >
              Log Out
            </MDBox>
          </MDBox>
        )}
      {/* </MDBox> */}
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
